"use client";

import { AnimatedTestimonials } from "@/components/ui/animated-testimonials";
import { extractStrapiMediaUrl } from "@/lib/strapi";
import { useTryUsProducts } from "@/hooks/product";
import frame20 from "@/public/assets/frame-20.png";
import frame21 from "@/public/assets/frame-21.png";
import frame22 from "@/public/assets/frame-22.png";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function extractStrapiOriginalMediaUrl(value: unknown): string | null {
  if (!value) return null;
  if (typeof value === "string") return extractStrapiMediaUrl(value);

  if (Array.isArray(value)) {
    for (const item of value) {
      const url = extractStrapiOriginalMediaUrl(item);
      if (url) return url;
    }
    return null;
  }

  if (!isRecord(value)) return null;

  const directUrl = value.url;
  if (typeof directUrl === "string" && directUrl) return extractStrapiMediaUrl(directUrl);

  const data = value.data;
  if (data) return extractStrapiOriginalMediaUrl(data);

  const attributes = value.attributes;
  if (attributes) return extractStrapiOriginalMediaUrl(attributes);

  return null;
}

export function FeaturedSection() {
  const { products, isLoading } = useTryUsProducts({ withBrandFilter: true, pageSize: 5 });

  if (isLoading && products.length === 0) {
    return (
      <section className="mx-auto w-full px-5">
        <div className="mx-auto max-w-5xl animate-pulse overflow-hidden rounded-3xl bg-primary p-2 text-background sm:p-6 md:h-[648px]">
          <div className="relative grid h-full grid-cols-1 items-center gap-4 md:grid-cols-[0.8fr_1.2fr] lg:grid-cols-[0.7fr_1.3fr] xl:grid-cols-[0.65fr_1.35fr]">
            <div className="h-full">
              <div className="relative h-[clamp(220px,55vw,420px)] w-full sm:h-[clamp(260px,65vw,560px)] md:h-full">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative h-full aspect-[9/16] max-w-full overflow-hidden rounded-3xl bg-white/10" />
                </div>
              </div>
            </div>

            <div className="flex h-full flex-col items-center justify-between gap-3 py-1 text-center sm:gap-4 sm:py-24">
              <div className="h-5 w-48 rounded-lg bg-white/10" />

              <div className="flex h-full w-full flex-col items-center justify-center gap-4">
                <div className="h-10 w-[min(420px,90%)] rounded-xl bg-white/10" />
                <div className="w-full space-y-3">
                  <div className="h-4 w-full rounded-lg bg-white/10" />
                  <div className="h-4 w-[92%] rounded-lg bg-white/10" />
                  <div className="h-4 w-[80%] rounded-lg bg-white/10" />
                </div>
                <div className="h-10 w-32 rounded-full bg-white/10" />
              </div>

              <div className="mt-6 flex items-center justify-center gap-4 sm:mt-8 md:mt-0">
                <div className="h-9 w-9 rounded-full bg-white/10 sm:h-10 sm:w-10" />
                <div className="h-9 w-9 rounded-full bg-white/10 sm:h-10 sm:w-10" />
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const fallbackTestimonials = [
    {
      designation: "Popular Products",
      name: "Regale Eau De Parfum 50ml",
      description:
        "Romano EDP 50ml Regale brings an effortlessly elegant and masculine scent. It opens with fresh bergamot, followed by smooth notes of lavender and sage leaves. The base of cedarwood and vanilla adds warmth and class, giving you a refined, confident presence that feels naturally stylish.",
      src: frame20.src,
      ctaLabel: "View Product",
      ctaHref: "/products",
    },
    {
      designation: "Popular Products",
      name: "Force Eau De Toilette",
      description:
        "ROMANO Force Eau De Toilette is created for dynamic men. Its contrasting woody fougère fragrance, combined with the luxurious freshness of geranium and cedarwood, creates a modern, masculine impression that lasts up to 8 hours.",
      src: frame21.src,
      ctaLabel: "View Product",
      ctaHref: "/products",
    },
    {
      designation: "Popular Products",
      name: "Attitude Eau De Toilette",
      description:
        "ROMANO ATTITUDE Eau de Toilette is created for modern, masculine men. The contrast of a fougère oriental fragrance with the warm blend of guaiac wood and sweet amber delivers a luxurious, captivating impression, lasting up to 8 hours.",
      src: frame22.src,
      ctaLabel: "View Product",
      ctaHref: "/products",
    },
  ];

  const mappedTestimonials = products.map((entity, index) => {
    const attributes = entity.attributes as {
      name?: string | null;
      slug?: string | null;
      short_description?: null;
      description?: string | null;
      product_banner?: unknown;
      cover_image?: unknown;
      size?: unknown;
      product_category?: { name?: string | null; slug?: string | null } | null;
    };

    const sizeLabel = (() => {
      const value = attributes.size;
      if (value === null || value === undefined) return null;
      if (typeof value === "number") return `${value}ml`;
      if (typeof value !== "string") return String(value);
      const trimmed = value.trim();
      if (!trimmed) return null;
      const lowered = trimmed.toLowerCase();
      if (lowered.endsWith("ml")) return lowered.replace(/\s+/g, "");
      return trimmed;
    })();

    const rawName = attributes.name ?? attributes.slug ?? String(entity.id);
    const name = rawName;
    const quote =
      attributes.short_description ??
      "Discover Romano's signature fragrance crafted for modern confidence.";
    const src =
      extractStrapiOriginalMediaUrl(attributes.product_banner) ??
      extractStrapiMediaUrl(attributes.product_banner) ??
      extractStrapiOriginalMediaUrl(attributes.cover_image) ??
      extractStrapiMediaUrl(attributes.cover_image) ??
      (index === 1 ? frame21.src : index === 2 ? frame22.src : frame20.src);
    const designation =
      attributes.product_category?.name ?? attributes.product_category?.slug ?? "Popular Products";
    const productSlug = attributes.slug ?? null;
    const description = attributes.description;

    const ctaHref =
      productSlug && sizeLabel
        ? `/products/${productSlug}?size=${encodeURIComponent(sizeLabel)}`
        : productSlug
          ? `/products/${productSlug}`
          : "/products";
    return {
      designation,
      description,
      name,
      src,
      ctaLabel: "View Product",
      ctaHref,
    };
  });

  
  const testimonials = mappedTestimonials.length ? mappedTestimonials : fallbackTestimonials;

  return (
    <section className="mx-auto w-full px-5">
      <AnimatedTestimonials
        className="mx-auto max-w-5xl max-h-5xl"
        autoplay
        testimonials={testimonials}
      />
    </section>
  );
}
